<script setup lang="ts">
import LoginCard from '@/views/auth/components/LoginCard.vue'
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { defineEmits, ref } from 'vue'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { useForm } from 'vee-validate'
import { Input } from '@/components/ui/input'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-vue-next'
import { AuthApi } from '@/services/api/auth.ts'

const wasSucceed = ref(true)
const emit = defineEmits<{
  (event: 'back'): void
}>()

const formSchema = toTypedSchema(
  z.object({
    email: z
      .string()
      .email('Wrong email format')
      .min(1, 'Email is required'),
  }),
)

const form = useForm({
  validationSchema: formSchema,
})

const onSubmit = form.handleSubmit(async (values) => {
  try {
    await AuthApi.requestPasswordReset(values)
    wasSucceed.value = false
  } catch {
    // TODO: Sentry log error.
  }
})
</script>

<template>
  <LoginCard>
    <CardHeader>
      <img
        src="/src/assets/media/revio/revio.svg"
        class="shrink-0 h-6 text-center p-0 m-0 mb-2.5"
        alt="Revio"
      />
      <CardTitle class="text-center">Forgot Password</CardTitle>
      <CardDescription class="text-center">Enter your email to reset your password</CardDescription>
    </CardHeader>
    <div
      v-if="!wasSucceed"
      class="mb-4 p-3 text-green-700 bg-green-100 border border-green-400 rounded-lg"
    >
      If a matching account was found an email was sent to allow you to reset your password
    </div>
    <form @submit="onSubmit">
      <FormField v-slot="{ componentField }" name="email">
        <FormItem class="mb-4">
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input id="email" type="email" v-bind="componentField" placeholder="Enter email" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <Button v-if="wasSucceed" class="mt-4 w-full" type="submit">Continue</Button>
      <Button v-if="wasSucceed" variant="ghost" class="back-button" @click.prevent="emit('back')">
        <ArrowLeft />
        Back to Login
      </Button>
      <Button v-if="!wasSucceed" class="mt-4 w-full" @click.prevent="emit('back')">
        <ArrowLeft />
        Back to Login
      </Button>
    </form>
  </LoginCard>
</template>

<style scoped>
.back-button {
  @apply mt-4 w-full text-gray-600;
}
</style>
